import { createContext, useContext, useMemo, useRef, type ReactNode } from 'react';

import { useCustomAppClient } from 'api/custom-client';

import { isLoginEnabled } from '../authMode';
import { installAssetDebug } from './assetDebug';
import { configureSasTokenSource } from './blobAssets';
import { createCatalogProvider, createClientJson, fetchJson, type CatalogProvider } from './provider';

/**
 * Makes the catalog data provider available to the app - and it is a React component on purpose.
 *
 * provider.ts used to fetch the access token itself, at module scope, via `OidcClient.get()` from
 * @axa-fr/react-oidc. That is the library's low-level entry point, and with two copies of that
 * library in the dependency tree (@uag/react-core brings its own, package.json declares another)
 * it reads a register that only ITS copy is written to - while @uag/react-core's `<AuthProvider>`
 * registers into the other copy through the high-level `<OidcProvider>`. The token was therefore
 * never found, no matter how long the provider waited for it.
 *
 * Deduplicating the two copies would mean touching DOKA's pipeline, which is not ours to change.
 * So the second entry point is gone instead: the token now comes from `useCustomClient`
 * (@uag/react-core, wrapped as `useCustomAppClient` in api/custom-client.tsx) - the same official
 * module the orval-generated API hooks use. Whichever copy of @axa-fr/react-oidc @uag/react-core
 * talks to internally is then simply not our concern: we never address that library ourselves.
 *
 * Because a hook can only be called from a component, the provider is composed here and handed
 * down through context - see `useCatalogProvider` below, which queries.ts and CompareModal use in
 * place of the former module-level `catalogProvider` singleton.
 */
const CatalogProviderContext = createContext<CatalogProvider | undefined>(undefined);

/**
 * The catalog provider for the current app configuration (REST or static export, plus any
 * `?apitest=` overrides). Throws rather than falling back to an unauthenticated provider when the
 * context is missing: a silent fallback would just reproduce the old symptom - every protected
 * route answering 401 - instead of naming the actual mistake.
 */
export const useCatalogProvider = (): CatalogProvider => {
    const provider = useContext(CatalogProviderContext);

    if (!provider) {
        throw new Error('useCatalogProvider() needs a <CatalogDataProvider> ancestor - see App.tsx.');
    }

    return provider;
};

/**
 * Login enabled: catalog requests go through @uag/react-core's request function, which attaches
 * the bearer token (and renews it) for us.
 *
 * The provider is built exactly once, against a ref rather than the current render's request
 * function: `useCustomClient` returns a fresh function identity on every render, and rebuilding
 * the provider along with it would hand every consumer a new object each time. The indirection
 * keeps the provider stable while every call still uses the newest request function - and with it
 * the newest token.
 */
const AuthenticatedCatalogDataProvider = ({ children }: { children: ReactNode }) => {
    const request = useCustomAppClient<unknown>();
    const requestRef = useRef(request);

    requestRef.current = request;

    const provider = useMemo(() => {
        // Same request function, same token - but NOT through `createClientJson`: the SAS endpoint
        // answers with a bare string inside its envelope, which that fetcher rejects on purpose
        // (see finishPayload). blobAssets.ts unwraps the envelope itself.
        configureSasTokenSource((url) =>
            requestRef.current({ url, method: 'GET', headers: { Accept: 'application/json' } })
        );

        const created = createCatalogProvider(createClientJson((config) => requestRef.current(config)));
        // `window.pih` - console diagnostics for the asset chain (config -> index -> SAS token ->
        // blob), see assetDebug.ts. Registered here because this is where the authenticated
        // provider exists; read-only, and deliberately available in a deployed build too, since a
        // private container and a real login are exactly what a local run cannot reproduce.
        installAssetDebug(created);

        return created;
    }, []);

    return <CatalogProviderContext.Provider value={provider}>{children}</CatalogProviderContext.Provider>;
};

/**
 * Login disabled (see authMode.ts - by default the static/offline export, which runs with no
 * backend at all): plain fetch, no token, no OIDC context touched. `useCustomClient` is not called
 * here at all, because without an `<AuthProvider>` ancestor there is no OIDC context for it to read
 * - the same reason UserGreeting and AppInsightsContextProvider are not mounted in that case.
 */
const AnonymousCatalogDataProvider = ({ children }: { children: ReactNode }) => {
    const provider = useMemo(() => {
        // Only reached with a private asset container AND login off - an unusual combination (the
        // SAS endpoint is scope-protected and would answer 401), but wiring it keeps the two
        // variants symmetrical: a public container/CDN or the offline bundle needs no token at all,
        // and blobAssets.ts then never calls this.
        configureSasTokenSource(async (url) => (await fetch(url, { headers: { Accept: 'application/json' } })).json());

        const created = createCatalogProvider(fetchJson);
        installAssetDebug(created);

        return created;
    }, []);

    return <CatalogProviderContext.Provider value={provider}>{children}</CatalogProviderContext.Provider>;
};

/**
 * `isLoginEnabled` comes from the build/URL configuration and never changes at runtime, so picking
 * the variant here (rather than branching inside one component) is stable - React sees the same
 * component type for the whole session and no hook is ever called conditionally.
 */
export const CatalogDataProvider = isLoginEnabled ? AuthenticatedCatalogDataProvider : AnonymousCatalogDataProvider;
